import { CalendarCheck } from 'lucide-react'
import { EmptyState, ErrorState } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useEmployeeLeaveBalances } from '@/features/leave'
import type { BalanceView } from '@/features/leave'

function days(value: number): string {
  const rounded = Math.round(value * 100) / 100
  return `${rounded} day${rounded === 1 ? '' : 's'}`
}

function BalanceCard({ balance }: { balance: BalanceView }) {
  // An unbounded type has no limit to measure against; a bar would draw one that is not there.
  const share = !balance.unbounded && balance.entitled > 0
    ? Math.min(100, Math.max(0, ((balance.used + balance.pending) / balance.entitled) * 100))
    : null

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[var(--color-heading)]">
            {balance.leaveTypeName}
          </p>
          <p className="text-xs text-[var(--color-muted)]">{balance.leaveTypeCode}</p>
        </div>
        <div className="text-end">
          <p className="text-xl font-semibold tabular-nums text-[var(--color-heading)]">
            {balance.unbounded ? days(balance.used) : days(balance.available)}
          </p>
          <p className="text-xs text-[var(--color-muted)]">
            {balance.unbounded ? 'taken · no limit' : 'available'}
          </p>
        </div>
      </div>

      {share !== null && (
        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-page)]"
          role="img"
          aria-label={`${Math.round(share)}% of the entitlement used or requested`}
        >
          <div className="h-full rounded-full bg-[var(--color-primary)]" style={{ width: `${share}%` }} />
        </div>
      )}

      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <dt className="text-xs text-[var(--color-muted)]">Entitled</dt>
          <dd className="text-sm tabular-nums text-[var(--color-heading)]">
            {balance.unbounded ? '—' : balance.entitled}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted)]">Used</dt>
          <dd className="text-sm tabular-nums text-[var(--color-heading)]">{balance.used}</dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted)]">Pending</dt>
          <dd className="text-sm tabular-nums text-[var(--color-heading)]">{balance.pending}</dd>
        </div>
      </dl>
    </Card>
  )
}

/** This year's leave balances, one card per leave type. */
export function LeaveBalanceTab({ employeeId }: { employeeId: string }) {
  const year = new Date().getFullYear()
  const balances = useEmployeeLeaveBalances(employeeId, year)

  if (balances.isPending) return <Skeleton className="h-40" />
  if (balances.isError) {
    return <ErrorState error={balances.error} onRetry={() => void balances.refetch()} />
  }
  if (balances.data.length === 0) {
    return (
      <EmptyState
        icon={CalendarCheck}
        title={`No leave balances for ${year}`}
        description="Balances appear once leave types are set up and accrual has run for this person."
      />
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--color-muted)]">
        Balances for {year}. Pending is requested but not yet approved, and is already held
        against what is available.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {balances.data.map((balance) => (
          <BalanceCard key={balance.leaveTypeId} balance={balance} />
        ))}
      </div>
    </div>
  )
}
